<template>
  <section class="paybox-content-container">
    <section class="left-side">
      <pay-box-purchase-info
        ref="payBoxPurchaseInfo"
        :get-height="getHeight"
        :tab-height="windowSize.tabHeight"
      />
    </section>

    <section class="right-side">
      <pay-box-items-list :get-height="getHeight" />
    </section>

    <section
      v-if="needCashUp"
      class="block-pdv"
    >
      <div class="content-center">
        <b-card-actions no-actions>
          <div>
            <p class="h3 text-danger">
              Caixa Bloqueado
            </p>
          </div>
          <div>
            <p>
              Valor no caixa ultrapassou o limite permitido. <br>
              <b>Por favor, realize a sangria para liberar o caixa</b>
            </p>
          </div>
        </b-card-actions>
      </div>
    </section>
  </section>
</template>

<script>
import { debounce } from 'lodash'
import { app, keyboardCode, loadingOverlay } from '@/mixins'
import { mapActions, mapGetters } from 'vuex'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import PayBoxPurchaseInfo from './components/PayBoxPurchaseInfo.vue'
import PayBoxItemsList from './components/PayBoxItemsList.vue'

export default {
  components: {
    PayBoxPurchaseInfo,
    PayBoxItemsList,
    BCardActions,
  },

  mixins: [app, keyboardCode, loadingOverlay],

  data() {
    return {
      windowSize: {
        height: window.document.body.clientHeight,
        tabHeight: 0,
      },
      hideAside: true,
      asideTimer: null,
      unsubscribe: null,
      helperTypes: {
        cashUp: 'cashUp',
        reinforcement: 'reinforcement',
      },
    }
  },

  computed: {
    ...mapGetters('pages/pdv', ['needCashUp']),
    ...mapGetters('pages/pdv/payBoxConfiguration', ['currentPayboxConfiguration']),

    getHeight() {
      // subtrai height do footer e header
      return this.windowSize.height - 154
    },
  },

  watch: {
    needCashUp(isBlocked) {
      if (isBlocked) {
        this.$refs.payBoxPurchaseInfo.purchaseInfoRemoveEvents()
      } else {
        this.$refs.payBoxPurchaseInfo.purchaseInforAddEvents()
      }
    },
  },

  async mounted() {
    try {
      this.showLoadingOverlay(this.$t('Preparando o caixa'))
      await this.getRewardConfiguration()

      // Carrega promoções SpendAndGet ativas da loja para o reprocessador local
      // de brindes (pay-box.reprocessSpendAndGetRewards). A regra é baseada na
      // tabela EFETIVA da venda (ver pages/pdv/effectivePriceTable). A tabela
      // do cliente sozinha não é usada — quando ela não pode reger o canal
      // (ATM com activeAtm=false, tabela inativa), a default é usada.
      const storeId = this.currentPayboxConfiguration?.store?.id
      if (storeId) {
        await this.$store.dispatch(
          'pages/pdv/effectivePriceTable/ensureDefaultPriceTable',
          { storeId }
        )
        const effective = this.$store.getters['pages/pdv/effectivePriceTable/effectivePriceTable']
        await this.$store.dispatch(
          'pages/pdv/spendAndGetPromotions/fetchActivePromotions',
          {
            storeId,
            priceTableId: effective?.id ?? null,
          }
        )
        // Reprocessa a venda atual após carregar as promoções (caso já existam itens
        // no carrinho de uma sessão restaurada).
        await this.$store.dispatch('pages/pdv/payBox/reprocessSpendAndGetRewards')
      }

      window.addEventListener('resize', this.getDimensions)
      this.getDimensions()
      setTimeout(() => {
        this.windowSize.tabHeight = this.getTabHeight()
      }, 300)
    } catch (error) {
      this.showError({ error })
    } finally {
      this.hideLoadingOverlay()
    }
  },

  created() {
    this.unsubscribe = this.$store.subscribe(async mutation => {
      if (mutation.type.startsWith('pages/pdv/payBox')) {
        await this.$store.dispatch('pages/pdv/payBox/updatePdvSession', {})
      }

      // Quando a venda é restaurada do IDB ou carregada do sidebar
      // (`SET_ALL_STATE`), os itens podem chegar sem o brinde de SpendAndGet
      // (pedido salvo antes da feature, ou o backend ainda não persistiu o
      // brinde). Disparamos o reprocesso para garantir que o brinde seja
      // inserido localmente. O reprocesso é serializado via mutex modular
      // em pay-box.js, portanto chamadas concorrentes não se entrelaçam.
      if (mutation.type === 'pages/pdv/payBox/SET_ALL_STATE') {
        await this.$store.dispatch('pages/pdv/payBox/reprocessSpendAndGetRewards')
      }
    })
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },

  destroyed() {
    window.removeEventListener('resize', this.getDimensions)
  },

  methods: {
    ...mapActions('pages/pdv', ['loadPdvData']),
    ...mapActions('reward', ['getRewardConfiguration']),

    // eslint-disable-next-line func-names
    getDimensions: debounce(function () {
      this.windowSize.height = window.document.body.clientHeight
      this.windowSize.tabHeight = this.getTabHeight()
    }, 300),

    getTabHeight() {
      // pego o id gerado pelo bootstrap
      return document.querySelector('#purchaseInfoTab__BV_tab_controls_')?.clientHeight || 0
    },

    async onShowSidebar(type) {
      this.$refs.sidebarHelper.onShowSidebar(type)
    },

    onHideAside(isLeave) {
      clearTimeout(this.asideTimer)
      this.asideTimer = setTimeout(() => {
        this.hideAside = isLeave
      }, 400)
    },
  },
}
</script>

<style lang="scss" scoped>
.paybox-content-container {
  // height: 100vh;
  display: grid;
  grid-template-columns: 46% 54%;
  grid-template-rows: 1fr;

  // TODO corrigir pensando q left e right side estejam dentro de uma grid que não seja do layout
  .left-side {
    grid-area: 1 / 1 / 2 / 2;
    border-right: 4px solid var(--dark);
  }

  .right-side {
    grid-area: 1 / 2 / 2 / 3;
    border-left: 4px solid var(--dark);
  }

  .block-pdv {
    grid-area: 1 / 1 / 2 / 3;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 3;
  }
}
</style>
